import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Monitor, Sparkles, Table2 } from 'lucide-react';
import ProspectingSearchesPanel from '../components/Prospecting/ProspectingSearchesPanel';
import ProspectingSavedListTable from '../components/Prospecting/ProspectingSavedListTable';
import { loadProspectingSavedLists } from '../components/Prospecting/prospectingApi';
import { useToast } from '../context/ToastContext';

function NarrowScreenNotice() {
  return (
    <div className="xl:hidden h-screen flex items-center justify-center bg-white dark:bg-gray-900 px-6">
      <div className="max-w-sm text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 mb-3">
          <Monitor className="w-5 h-5 text-gray-400" />
        </div>
        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          Open Prospecting on a wider screen
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The three-panel workspace is designed for desktop. Rotate to landscape or open on a larger display to use it.
        </p>
      </div>
    </div>
  );
}

export default function ProspectingSavedListWorkspace() {
  const navigate = useNavigate();
  const { listId } = useParams();
  const { showError } = useToast();
  const [searchesCollapsed, setSearchesCollapsed] = useState(false);
  const [savedLists, setSavedLists] = useState([]);
  const [savedListsLoading, setSavedListsLoading] = useState(true);
  const [savedListsReloadToken, setSavedListsReloadToken] = useState(0);
  const [savedListReloadToken, setSavedListReloadToken] = useState(0);

  const reloadSavedLists = useCallback(() => {
    setSavedListsReloadToken((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setSavedListsLoading(true);

    loadProspectingSavedLists({ signal: controller.signal })
      .then((lists) => {
        setSavedLists(Array.isArray(lists) ? lists : []);
        setSavedListsLoading(false);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setSavedListsLoading(false);
        showError(error?.message || 'Could not load saved lists');
      });

    return () => controller.abort();
  }, [savedListsReloadToken, showError]);

  const activeList = useMemo(
    () => savedLists.find((list) => String(list.id) === String(listId)) || null,
    [listId, savedLists]
  );

  useEffect(() => {
    if (savedListsLoading) return;
    if (activeList || !listId) return;

    if (savedLists.length > 0) {
      navigate(`/prospecting/lists/${savedLists[0].id}`, { replace: true });
      return;
    }

    navigate('/prospecting', { replace: true });
  }, [activeList, listId, navigate, savedLists, savedListsLoading]);

  const handleOpenSavedList = useCallback((nextListId) => {
    if (!nextListId) return;
    if (String(nextListId) === String(listId)) {
      setSavedListReloadToken((value) => value + 1);
      return;
    }
    navigate(`/prospecting/lists/${nextListId}`);
  }, [listId, navigate]);

  return (
    <>
      <NarrowScreenNotice />
      <div className="hidden xl:flex h-screen flex-col bg-white dark:bg-gray-900 overflow-hidden">
        <div className="h-12 flex items-center justify-between gap-3 px-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
          <div className="min-w-0 flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/prospecting')}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Home
            </button>
            <div className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300">
              <Table2 className="w-3 h-3" />
              Saved list
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {activeList?.name || 'Prospecting list'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/prospecting/new')}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Start new search
          </button>
        </div>
        <div className="flex-1 flex overflow-hidden">
          <ProspectingSearchesPanel
            activeSearchId={null}
            collapsed={searchesCollapsed}
            onToggle={() => setSearchesCollapsed((value) => !value)}
            savedLists={savedLists}
            savedListsLoading={savedListsLoading}
            activeSavedListId={listId}
            onOpenSavedList={handleOpenSavedList}
          />
          <ProspectingSavedListTable
            listId={listId}
            reloadToken={savedListReloadToken}
            onReloadSavedLists={reloadSavedLists}
          />
        </div>
      </div>
    </>
  );
}
